import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Option, OptionOnSelectData, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useCommonStyles } from '../../../common/hooks';
import { AwardNominationSummary, blankNominationScrubbingStatus, CountryNominationSummary, DisqualifyReasonType, disqualifyReasons, NominationScrubbingStatus } from '../../../models';
import { disqualificationRequestSchema } from '../../../models/schemas/disqualification-request.schema';
import { createValidationState } from '../../../shared/validation/zod-validation-state';
import { ValidationMessageList } from '../ValidationMessageList';
import { FormInput } from '../form/FormInput';
import { SelectOptions } from '../form/SelectOptions';

interface MultipleDisqualifyScrubbingModalProps {
  selectedNominationSummaries: AwardNominationSummary[] | CountryNominationSummary[];
  onUpdateScrubbingStatus(nominationId: number, scrubbingStatus: NominationScrubbingStatus): Promise<void>;
  onCancel(): void;
}

const useStyles = makeStyles({
  modalSurface: {
    maxWidth: '416px',
    width: '100%',
  },
  paragraph: {
    margin: '12px 0'
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }
});

const MultipleDisqualifyScrubbingModalImpl: React.FC<MultipleDisqualifyScrubbingModalProps> = (props) => {
  const {
    selectedNominationSummaries,
    onUpdateScrubbingStatus,
    onCancel,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [disqualifyReason, setDisqualifyReason] = useState<DisqualifyReasonType>('Incomplete');
  const [currentNominationIndex, setCurrentNominationIndex] = useState(0);
  const [feedback, setFeedback] = useState<string>();
  const [isBeingValidated, setIsBeingValidated] = useState<boolean>(false);

  const disqualificationValidation = createValidationState(disqualificationRequestSchema.safeParse({
    disqualifyReason,
    feedback,
  }));

  const handleDisqualifyReasonSelected = (data: OptionOnSelectData): void => {
    const selectedDisqualifyReason = disqualifyReasons.find((reason) => reason === data.optionValue);
    if (!selectedDisqualifyReason) return;

    setFeedback(undefined);
    setDisqualifyReason(selectedDisqualifyReason);
  };

  const handleUpdateScrubbingStatus = async (): Promise<void> => {
    setIsBeingValidated(true);
    if (!disqualificationValidation.isValid) return;

    const scrubbingStatus = blankNominationScrubbingStatus();
    scrubbingStatus.nominationId = +selectedNominationSummaries[currentNominationIndex].nominationId;
    scrubbingStatus.scrubbingStatus = 'Disqualified';
    scrubbingStatus.disqualifyReason = disqualifyReason;
    scrubbingStatus.feedback = feedback;
    await onUpdateScrubbingStatus(selectedNominationSummaries[currentNominationIndex].nominationId, scrubbingStatus);

    if (currentNominationIndex + 1 === selectedNominationSummaries.length) {
      onCancel();
      return;
    }
    setCurrentNominationIndex(currentNominationIndex + 1);
    setIsBeingValidated(false);
    setFeedback(undefined);
  };

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={mergeClasses(commonStyles.modalSurface, styles.modalSurface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={(
              <Button
                title='close'
                appearance='transparent'
                icon={<DismissRegular />}
                onClick={() => onCancel()}
              />
            )}
          >
            Disqualify
          </DialogTitle>
          <DialogContent className={styles.modalContent}>
            <Text
              className='text--blue'
              weight='semibold'
              block
            >
              {`${selectedNominationSummaries[currentNominationIndex].title} ${currentNominationIndex + 1} of ${selectedNominationSummaries.length}`}
            </Text>
            <Text
              as='p'
              block
              className={styles.paragraph}
            >
              Are you sure you would like to disqualify? If so, please provide reasoning and feedback.
            </Text>
            <SelectOptions
              label='Disqualify reason'
              onOptionSelect={handleDisqualifyReasonSelected}
              dropdownProps={{
                value: disqualifyReason,
                selectedOptions: [disqualifyReason],
              }}
            >
              {
                disqualifyReasons.map((reason) => (
                  <Option
                    key={reason}
                    value={reason}
                  >
                    {reason}
                  </Option>
                ))
              }
            </SelectOptions>
            {
              disqualifyReason === 'Other' && (
                <FormInput
                  label='Other'
                  required
                  validationMessage={isBeingValidated && disqualificationValidation.fieldErrors.feedback?.length
                    ? <ValidationMessageList messages={disqualificationValidation.fieldErrors.feedback} />
                    : undefined}
                  onChange={(value) => setFeedback(value)}
                  textAreaProps={{
                    value: feedback || '',
                  }}
                />
              )
            }
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={() => onCancel()}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={handleUpdateScrubbingStatus}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const MultipleDisqualifyScrubbingModal = observer(MultipleDisqualifyScrubbingModalImpl);